"use client"

import { CSSProperties, memo, ReactNode, useMemo } from "react"

import styles from "./index.module.scss"

interface GradientTextProps {
  children: ReactNode
  className?: string
  colors?: string[]
  animationSpeed?: number
  showBorder?: boolean
  direction?: "horizontal" | "vertical" | "diagonal"
  pauseOnHover?: boolean
  yoyo?: boolean
}

type GradientStyle = CSSProperties & {
  "--gradient-background": string
  "--gradient-size": string
  "--gradient-duration": string
  "--gradient-direction": "normal" | "alternate"
}

function GradientText({
  children,
  className = "",
  colors = ["#5227FF", "#FF9FFC", "#B19EEF"],
  animationSpeed = 8,
  showBorder = false,
  direction = "horizontal",
  pauseOnHover = false,
  yoyo = true,
}: GradientTextProps) {
  const gradientAngle =
    direction === "horizontal" ? "to right" : direction === "vertical" ? "to bottom" : "to bottom right"
  const gradientSize = direction === "horizontal" ? "300% 100%" : direction === "vertical" ? "100% 300%" : "300% 300%"
  const classNames = [
    styles["animated-gradient-text"],
    styles[direction],
    showBorder ? styles["with-border"] : "",
    pauseOnHover ? styles["pause-on-hover"] : "",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  const gradientStyle = useMemo<GradientStyle>(() => {
    // Duplicate first color at the end for seamless looping.
    const gradientColors = [...colors, colors[0]].join(", ")

    return {
      "--gradient-background": `linear-gradient(${gradientAngle}, ${gradientColors})`,
      "--gradient-size": gradientSize,
      "--gradient-duration": `${Math.max(animationSpeed, 0.001)}s`,
      "--gradient-direction": yoyo ? "alternate" : "normal",
    }
  }, [animationSpeed, colors, gradientAngle, gradientSize, yoyo])

  return (
    <div className={classNames} style={gradientStyle}>
      {showBorder && <div className={styles["gradient-overlay"]} />}
      <div className={styles["text-content"]}>{children}</div>
    </div>
  )
}

const areEqual = (prevProps: GradientTextProps, nextProps: GradientTextProps) => {
  const prevColors = prevProps.colors
  const nextColors = nextProps.colors
  const colorsAreEqual =
    prevColors === nextColors ||
    (!prevColors || !nextColors
      ? prevColors === nextColors
      : prevColors.length === nextColors.length && prevColors.every((color, index) => color === nextColors[index]))

  return (
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className &&
    prevProps.animationSpeed === nextProps.animationSpeed &&
    prevProps.showBorder === nextProps.showBorder &&
    prevProps.direction === nextProps.direction &&
    prevProps.pauseOnHover === nextProps.pauseOnHover &&
    prevProps.yoyo === nextProps.yoyo &&
    colorsAreEqual
  )
}

export default memo(GradientText, areEqual)
